// SourcingCampaignRenderer.tsx
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { Building2, Loader2, RefreshCw, Target, Trash2 } from '@/components/ui/lucide-memo';
import { BaseEntityProps, EntityRowAction, SourcingCampaignDTO, SourcingCampaignStatus } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { portalTableRow } from '@/components/portal/portalTheme';
import StopButton, { STOP_BUTTON_SIZE_INLINE } from '@/components/portal/StopButton';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';
import { campaignStatusPillClass, formatCampaignDateTime } from '#lib/leasing/campaignCard';
import { readCampaignRow } from '#lib/leasing/campaignRow';

const DEFAULT_ROW_COLUMN_ORDER = ['campaign', 'status', 'targetBrands', 'leads', 'updatedAt', 'actions'] as const;

// One frozen style object per alignment: the row maps over its columns, so a literal here would
// hand every cell a brand-new object on every render.
const CELL_JUSTIFY: Record<'start' | 'center' | 'end', React.CSSProperties> = {
  start: { justifyContent: 'flex-start' },
  center: { justifyContent: 'center' },
  end: { justifyContent: 'flex-end' },
};

const translations = {
  en: {
    untitled: 'Untitled campaign',
    stopCampaign: 'Abort',
    stoppingCampaign: 'Aborting...',
    reapplyRubric: 'Reapply',
    reapplyingRubric: 'Reapplying...',
    destroyCampaign: 'Delete',
    destroyingCampaign: 'Deleting...',
    destroyConfirm: (campaignLabel: string, leadsCount: number) =>
      `Delete campaign "${campaignLabel}" and delete ${leadsCount} related lead(s)? This cannot be undone.`,
    status: {
      PENDING: 'Pending',
      WAITING: 'Queued',
      RUNNING: 'Running',
      COMPLETE: 'Complete',
      FAILED: 'Failed',
      ABORTED: 'Aborted',
    } as Record<SourcingCampaignStatus, string>,
  },
  fr: {
    untitled: 'Campagne sans titre',
    stopCampaign: 'Arreter',
    stoppingCampaign: 'Arret...',
    reapplyRubric: 'Reappliquer',
    reapplyingRubric: 'Reapplication...',
    destroyCampaign: 'Supprimer',
    destroyingCampaign: 'Suppression...',
    destroyConfirm: (campaignLabel: string, leadsCount: number) =>
      `Supprimer la campagne "${campaignLabel}" et supprimer ${leadsCount} lead(s) lie(s) ? Action irreversible.`,
    status: {
      PENDING: 'En attente',
      WAITING: 'En file',
      RUNNING: 'En cours',
      COMPLETE: 'Terminee',
      FAILED: 'Echouee',
      ABORTED: 'Interrompue',
    } as Record<SourcingCampaignStatus, string>,
  },
};

const formatCampaignLabel = (campaign: SourcingCampaignDTO, fallback: string): string => {
  if (campaign.propertyName && campaign.propertyName.trim().length > 0) {
    return `${campaign.propertyName} - ${campaign.id.slice(0, 8)}`;
  }
  return `${fallback} ${campaign.id.slice(0, 8)}`;
};

export interface SourcingCampaignRendererProps extends BaseEntityProps<SourcingCampaignDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<SourcingCampaignDTO>[];
}

export const SourcingCampaignRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = [],
}: SourcingCampaignRendererProps) => {
  const glass = useTableGlass();
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const t = translations[lang];
  const data = useKernelStore((state) => readCampaignRow(state.inventory, entityId)) as SourcingCampaignDTO | undefined;
  const normalizedVariant = variant === 'page' ? 'row' : variant;
  const [isStopping, setIsStopping] = useState(false);
  const [isReapplying, setIsReapplying] = useState(false);
  const [isDestroying, setIsDestroying] = useState(false);

  const formatDate = useCallback((value?: string) => formatCampaignDateTime(value, lang), [lang]);

  const handleRowClick = useCallback(() => {
    if (onClick) {
      onClick();
      return;
    }
    if (!kernel.current) return;
    void kernel.current.focusEntityUI('SourcingCampaign', entityId);
  }, [onClick, kernel, entityId]);

  const handleStopCampaign = useCallback(async () => {
    if (!kernel.current || !data?.id || isStopping) return;
    setIsStopping(true);
    try {
      await kernel.current.stopSourcingCampaign(data.id);
    } finally {
      setIsStopping(false);
    }
  }, [data?.id, isStopping, kernel]);

  const handleDestroyCampaign = useCallback(async () => {
    if (!kernel.current || !data?.id || isDestroying) return;
    const campaignLabel = formatCampaignLabel(data, t.untitled);
    const leadsCount = typeof data.leadsCount === 'number' ? data.leadsCount : 0;
    const confirmed = window.confirm(t.destroyConfirm(campaignLabel, leadsCount));
    if (!confirmed) return;

    setIsDestroying(true);
    try {
      await kernel.current.destroySourcingCampaignWithLeads(data.id);
    } finally {
      setIsDestroying(false);
    }
  }, [data, isDestroying, kernel, t]);

  const handleReapplyRubric = useCallback(async () => {
    if (!kernel.current || !data?.id || isReapplying) return;
    setIsReapplying(true);
    try {
      await kernel.current.reapplySourcingCampaignRubric(data.id);
    } catch (error: any) {
      console.error('[SourcingCampaignRenderer] reapply rubric failed', error);
    } finally {
      setIsReapplying(false);
    }
  }, [data?.id, isReapplying, kernel]);

  // The row itself is clickable, so each action button has to swallow the click. Stable references
  // keep the memoized buttons out of the re-render path.
  const handleStopClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void handleStopCampaign();
  }, [handleStopCampaign]);

  const handleReapplyClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void handleReapplyRubric();
  }, [handleReapplyRubric]);

  const handleDestroyClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void handleDestroyCampaign();
  }, [handleDestroyCampaign]);

  if (!data) return <EntitySkeleton variant={normalizedVariant} className={className} />;

  const status = (data.status || 'PENDING') as SourcingCampaignStatus;
  const isActiveCampaign = status === 'RUNNING' || status === 'WAITING';
  const canDestroyCampaign = Boolean(userContext.isFaterAdmin);
  const targetBrandCount = Array.isArray(data.targetBrandIds) ? data.targetBrandIds.length : 0;
  const leadsCount = typeof data.leadsCount === 'number' ? data.leadsCount : 0;
  const visibleLeadsCount =
    typeof data.nonDiscardedLeadsCount === 'number'
      ? data.nonDiscardedLeadsCount
      : leadsCount;

  if (normalizedVariant === 'chip') {
    return (
      <div
        className={clsx('flex items-center gap-2 px-2 py-1 rounded-md bg-white/5 border border-white/10 text-xs text-white/80 cursor-pointer hover:bg-white/10', className)}
        onClick={handleRowClick}
      >
        <Target size={12} />
        <span className="truncate">{formatCampaignLabel(data as SourcingCampaignDTO, t.untitled)}</span>
      </div>
    );
  }

  if (normalizedVariant === 'card') {
    return (
      <div
        className={clsx('flex flex-col w-full h-full overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:bg-white/10 hover:border-white/20 cursor-pointer', className)}
        onClick={handleRowClick}
      >
        <div className="p-3 flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/20 text-indigo-400">
            <Target size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] text-white/40">{data.status}</div>
            <div className="text-xs font-medium text-white/90 truncate">{formatCampaignLabel(data as SourcingCampaignDTO, t.untitled)}</div>
          </div>
        </div>
      </div>
    );
  }

  const cellRenderers: Record<string, React.ReactNode> = {
    campaign: (
      <div className="min-w-0 h-full flex items-center gap-2">
        <Building2 size={12} className="shrink-0 text-white/45" />
        <span className="truncate font-medium text-white" title={formatCampaignLabel(data as SourcingCampaignDTO, t.untitled)}>
          {formatCampaignLabel(data as SourcingCampaignDTO, t.untitled)}
        </span>
      </div>
    ),
    status: (
      <div className="flex h-full items-center">
        <span className={clsx('rounded-full border px-2 py-0.5 text-[10px]', campaignStatusPillClass(status))}>
          {t.status[status]}
        </span>
      </div>
    ),
    targetBrands: (
      <div className="flex h-full items-center justify-end text-white/85">
        {targetBrandCount}
      </div>
    ),
    leads: (
      <div className="flex h-full items-center justify-end text-white/85">
        {visibleLeadsCount}
      </div>
    ),
    updatedAt: (
      <div className="flex h-full items-center text-white/60">
        <span className="truncate">{formatDate(data.updatedAt)}</span>
      </div>
    ),
    actions: (
      <div className="flex h-full items-center justify-end gap-2">
        {isActiveCampaign && (
          <StopButton
            onClick={handleStopClick}
            disabled={isStopping}
            size={STOP_BUTTON_SIZE_INLINE}
            label={isStopping ? t.stoppingCampaign : t.stopCampaign}
          />
        )}
        {!isActiveCampaign && (
          <button
            type="button"
            onClick={handleReapplyClick}
            disabled={isReapplying || isDestroying}
            className={clsx(
              'inline-flex shrink-0 items-center justify-center rounded-md border border-white/15 bg-white/[0.05] p-1.5 text-white/70 transition-colors',
              (isReapplying || isDestroying)
                ? 'cursor-not-allowed opacity-70'
                : 'hover:bg-white/10 hover:text-white/90'
            )}
            title={isReapplying ? t.reapplyingRubric : t.reapplyRubric}
            aria-label={t.reapplyRubric}
          >
            {isReapplying ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        )}
        {canDestroyCampaign && (
          <button
            type="button"
            onClick={handleDestroyClick}
            disabled={isDestroying || isActiveCampaign || isReapplying}
            className={clsx(
              'inline-flex shrink-0 items-center justify-center rounded-md border border-white/15 bg-white/[0.05] p-1.5 text-white/70 transition-colors',
              (isDestroying || isActiveCampaign || isReapplying)
                ? 'cursor-not-allowed opacity-70'
                : 'hover:bg-white/10 hover:text-white/90'
            )}
            title={isDestroying ? t.destroyingCampaign : t.destroyCampaign}
            aria-label={t.destroyCampaign}
          >
            {isDestroying ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
          </button>
        )}
        <EntityRowActions entityType="SourcingCampaign" entity={data} rowActions={rowActions} />
      </div>
    ),
  };

  const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));

  return (
    <div
      className={clsx(
        portalTableRow,
        styles.gridRow,
        styles.bodyRow,
        'group h-10 w-full cursor-pointer border-b border-[#2A2A2A] bg-[#181818] hover:bg-[#1E1E1E]',
        glass && TABLE_GLASS_ROW,
        className
      )}
      onClick={handleRowClick}
    >
      {activeColumns.map((column) => (
        <div
          key={column.id}
          className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
          style={
            column.id === 'actions' || column.align === 'right'
              ? CELL_JUSTIFY.end
              : column.align === 'center'
                ? CELL_JUSTIFY.center
                : CELL_JUSTIFY.start
          }
        >
          {cellRenderers[column.id] || null}
        </div>
      ))}
    </div>
  );
});

SourcingCampaignRenderer.displayName = 'SourcingCampaignRenderer';
